import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Activity, AlertCircle } from "lucide-react";
import { PatientSmartfileRecordsList } from "./PatientSmartfileRecordsList";
import { supabase } from "@/integrations/supabase/client";

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface GalleryImage {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  source: "upload" | "camera";
  ai_summary?: string | null;
  created_at: string;
}

interface PatientSmartfileRecordsProps {
  patientId: string;
  sessions: SmartfileSession[];
  onRefresh: () => void;
}

export function PatientSmartfileRecords({ 
  patientId, 
  sessions, 
  onRefresh
}: PatientSmartfileRecordsProps) {
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);

  useEffect(() => {
    loadGalleryImages();
  }, [patientId]);

  async function loadGalleryImages() {
    try {
      const { data, error } = await supabase
        .from("patient_gallery")
        .select("id, file_name, file_path, file_type, source, ai_summary, created_at")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error('❌ Error loading gallery images:', error);
      } else {
        setGalleryImages((data || []).map(item => ({
          ...item,
          source: item.source as "upload" | "camera"
        })));
        console.log('✅ Gallery images loaded:', data?.length || 0);
      }
    } catch (error) {
      console.error('❌ Error loading gallery images:', error);
    }
  }

  console.log(`🔍 [PatientSmartfileRecords] Received ${sessions.length} sessions and ${galleryImages.length} images for patient ${patientId}`);
  
  // ✅ FILTRO PRIMÁRIO: Excluir documentos do tipo "other"
  const sessionsWithoutOther = sessions.filter(session => {
    if (session.content_type === 'other') {
      console.log(`⚠️ [PatientSmartfileRecords] Excluding session ${session.id} (${session.file_name}) - content_type is "other"`);
      return false;
    }
    return true;
  });
  
  // Filtrar sessões que têm structured_records (qualquer tipo, não apenas lab_result)
  const sessionsWithRecords = sessionsWithoutOther.filter(session => {
    const hasRecords = session.structured_records && Array.isArray(session.structured_records) && session.structured_records.length > 0;
    
    if (!hasRecords) {
      console.log(`⚠️ Session ${session.id} (${session.file_name}) has no structured_records`);
    } else {
      const labResultsCount = session.structured_records.filter((record: any) => 
        typeof record === 'object' && record !== null && 'type' in record && record.type === 'lab_result'
      ).length;
      console.log(`✅ Session ${session.id} (${session.file_name}): ${labResultsCount} lab_results out of ${session.structured_records.length} total records`);
    }
    
    return hasRecords;
  });

  // Filtrar sessões que têm structured_records com lab_result OU training_plan OU meal_plan OU physiotherapy_evolution OU anthropometry
  const sessionsWithRelevantRecords = sessionsWithRecords.filter(session => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return false;
    }
    
    const hasRelevantRecord = session.structured_records.some((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      const recordType = record.type;
      if (recordType === 'meal_plan') {
        // Para meal_plan, verificar se é weekly ou food e não é main_record (pode ser boolean ou string)
        const mealPlanRecord = record as { structure_type?: string; is_main_record?: boolean | string };
        const isMainRecord = mealPlanRecord.is_main_record === true || mealPlanRecord.is_main_record === 'true';
        const isValid = (mealPlanRecord.structure_type === 'weekly' || mealPlanRecord.structure_type === 'food') && !isMainRecord;
        if (isValid) {
          console.log(`✅ [PatientSmartfileRecords] Found valid meal_plan:`, {
            structure_type: mealPlanRecord.structure_type,
            is_main_record: mealPlanRecord.is_main_record,
            isMainRecord,
            isValid
          });
        }
        return isValid;
      }
      if (recordType === 'physiotherapy_evolution') {
        // Para physiotherapy_evolution, verificar se não é main_record
        const physioRecord = record as { is_main_record?: boolean | string };
        const isMainRecord = physioRecord.is_main_record === true || physioRecord.is_main_record === 'true';
        return !isMainRecord;
      }
      if (recordType === 'anthropometry') {
        // Para anthropometry, verificar se não é main_record
        const anthropometryRecord = record as { is_main_record?: boolean | string };
        const isMainRecord = anthropometryRecord.is_main_record === true || anthropometryRecord.is_main_record === 'true';
        return !isMainRecord;
      }
      if (recordType === 'anamnesis') {
        // Para anamnesis, verificar se não é main_record (similar a physiotherapy_evolution)
        const anamnesisRecord = record as { is_main_record?: boolean | string };
        const isMainRecord = anamnesisRecord.is_main_record === true || anamnesisRecord.is_main_record === 'true';
        return !isMainRecord;
      }
      if (recordType === 'medical_report') {
        // Para medical_report, verificar se não é main_record
        const medicalReportRecord = record as { is_main_record?: boolean | string };
        const isMainRecord = medicalReportRecord.is_main_record === true || medicalReportRecord.is_main_record === 'true';
        return !isMainRecord;
      }
      if (recordType === 'biopsy_report') {
        // Para biopsy_report, verificar se não é main_record
        const biopsyRecord = record as { is_main_record?: boolean | string };
        const isMainRecord = biopsyRecord.is_main_record === true || biopsyRecord.is_main_record === 'true';
        return !isMainRecord;
      }
      return recordType === 'lab_result' || recordType === 'training_plan' || recordType === 'vital_sign' || recordType === 'anatomopathology';
    });
    
    if (hasRelevantRecord) {
      console.log(`✅ [PatientSmartfileRecords] Session ${session.id} has relevant records`);
    }
    
    return hasRelevantRecord;
  });

  console.log(`✅ [PatientSmartfileRecords] Filtered to ${sessionsWithRelevantRecords.length} sessions with lab_results, training_plans, meal_plans, physiotherapy_evolution, anthropometry, anamnesis, vitals or medical_reports (out of ${sessionsWithRecords.length} with any records)`);

  const totalItems = sessionsWithRelevantRecords.length + galleryImages.length;

  if (totalItems === 0) {
    // Mostrar informações de debug
    console.log(`📊 Debug info:`, {
      totalSessions: sessions.length,
      sessionsWithRecords: sessionsWithRecords.length,
      galleryImages: galleryImages.length,
      sessionsInfo: sessions.map(s => ({
        id: s.id,
        file_name: s.file_name,
        status: s.status,
        hasRecords: !!s.structured_records,
        isArray: Array.isArray(s.structured_records),
        recordsCount: Array.isArray(s.structured_records) ? s.structured_records.length : 0,
        recordTypes: Array.isArray(s.structured_records) 
          ? [...new Set(s.structured_records.map((r: any) => 
              typeof r === 'object' && r !== null && 'type' in r ? r.type : 'any'
            ))]
          : []
      }))
    });
    
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center space-y-3">
            <Activity className="h-12 w-12 mx-auto text-muted-foreground/50" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Nenhum registro de saúde encontrado
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Adicione registros clínicos, capture documentos ou envie imagens para começar
              </p>
              {sessions.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  ({sessions.length} sessão(ões) encontrada(s), {sessionsWithRecords.length} com registros estruturados, mas nenhuma com resultados laboratoriais, planos de treino, planos alimentares, evoluções fisioterapêuticas, antropometria, anamnese, laudos médicos ou sinais vitais)
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 w-full max-w-full overflow-x-hidden">
      <PatientSmartfileRecordsList 
        sessions={sessionsWithRelevantRecords}
        galleryImages={galleryImages}
        onRefresh={() => {
          onRefresh();
          loadGalleryImages();
        }}
      />
      
      {totalItems > 0 && (
        <div className="text-xs text-center text-muted-foreground pt-2">
          Exibindo {totalItems} registro{totalItems !== 1 ? 's' : ''} ({sessionsWithRelevantRecords.length} sessão{sessionsWithRelevantRecords.length !== 1 ? 'ões' : ''} e {galleryImages.length} imagem{galleryImages.length !== 1 ? 'ns' : ''})
        </div>
      )}
    </div>
  );
}

